import { ExportToCsv } from "export-to-csv";
import {
  CoachComponent,
  ExportPlayerObject,
  CompleteAccount,
  ExportCoachObject,
  PlayerSearchResponse,
  SubscribedPlayerSearchResponse,
  ExportAdminPlayerObject,
  VideoProjectPlayerUsage,
  ComplianceExportEntry,
  ExportComplianceEntryObject,
} from "@/interfaces";
import { userFullName, getStatusText, getSubscriptionType, getFullName } from "./helpers";
import moment from "moment-timezone";
import {
  AssistantCoach,
  LeaderboardMetric,
  LeaderboardResult,
  PlayerSearchResult,
  PlayerSegmentation,
  PlayerSegmentationType,
  StatsResponse,
} from "@/services/bedrock";
import { URLS } from "./routes";
import { camelCase, capitalize } from "lodash";

const DEFAULT_CSV_OPTIONS = {
  fieldSeparator: ",",
  quoteStrings: '"',
  decimalSeparator: ".",
  showLabels: true,
  showTitle: true,
  useTextFile: false,
  useBom: true,
};

/**
 * Generate a list of ExportPlayerObject to be exported in a csv file
 * @param players List of SubscribedPlayerWithStatsSearchResponse with the data
 * @returns list of ExportPlayerObject to be exported in a csv file
 */
export function exportPlayerList(players: PlayerSearchResult[]) {
  const exportData: ExportPlayerObject[] = [];

  const segmentNames = (segments: PlayerSegmentation[], type: PlayerSegmentationType) => {
    return segments
      .filter(s => s.type === type)
      .map(s => s.name)
      .join(", ");
  };

  players.map(player => {
    const activeSns = player.activeSocialNetworkProfiles.map(s => s.socialNetworkId);
    const playerInfo: ExportPlayerObject = {
      id: player.guid || "-",
      name: player.fullName || "-",
      email: player.email || "-",
      phone: player.phone || "-",
      internalIdentifier: player.internalIdentifier || "",
      subscriptionStatus: getStatusText(player.planStatus),
      subscriptionTier: getSubscriptionType(player.tier),
      createdDate: toDateTime(player.createdDate),
      signUpDate: toDateTime(player.verificationDate),
      lastLoginDate: toDateTime(player.lastLoginDate),
      canceledDate: toDateTime(player.canceledAt),
      branches: segmentNames(player.segmentationDetails, "BRANCH"),
      regions: segmentNames(player.segmentationDetails, "REGION"),
      costCenters: segmentNames(player.segmentationDetails, "COST_CENTER"),
      complianceDisclaimer: player.complianceDisclaimer,
      totalSocialNetworks: player.activeSocialNetworkProfiles.length || "-",
      facebookConnected: activeSns.includes("FB") ? "Y" : "N",
      instagramConnected: activeSns.includes("IG") ? "Y" : "N",
      linkedinConnected: activeSns.includes("LI") ? "Y" : "N",
      xConnected: activeSns.includes("TW") ? "Y" : "N",
      tiktokConnected: activeSns.includes("TT") ? "Y" : "N",
    };

    exportData.push(playerInfo);
  });

  if (exportData.length === 0) {
    return;
  }
  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename: `SocialCoachPlayers`,
    title: `SocialCoach Players`,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(exportData);
}

const assistantInfo = (assistant: AssistantCoach) => ({
  id: assistant.guid || "-",
  name: assistant.fullName || "-",
  email: assistant.email || "-",
  status: assistant.status === "SUSPENDED" ? "Cancelled" : capitalize(assistant.status),
  scoped: assistant.isScoped ? "Y" : "N",
  regions: (assistant.regions || []).map(r => r.name).join(", "),
  branches: (assistant.branches || []).map(b => b.name).join(", "),
  assignedPlayers: assistant.explicitPlayerCount ?? 0,
  assignedPlayerEmails: (assistant.explicitPlayerEmails || []).join(", "),
  createdDate: toDateTime(assistant.createdDate),
  lastLoginDate: toDateTime(assistant.lastLoginDate),
});

const PERMISSION_LEVEL_LABELS: Record<string, string> = { NONE: "None", READ: "Read", WRITE: "Write" };

const assistantPermissions = (assistant: AssistantCoach, components: CoachComponent[]) => {
  const levels = new Map((assistant.componentPermissions || []).map(p => [p.componentId, p.permissionLevel]));
  // Every row gets the same columns, since the CSV takes its headers from the first row
  return Object.fromEntries(
    components.map(c => {
      const level = levels.get(c.id) || "NONE";
      return [`Permission: ${c.label}`, PERMISSION_LEVEL_LABELS[level] || level];
    })
  );
};

/**
 * Generates a csv file with the coach's Assistants, including the guid used to reference them in the API
 * @param assistants List of AssistantCoach, fetched with the regions, branches, explicitPlayerCount,
 * explicitPlayerEmails and componentPermissions extra fields
 * @param components The components to export a permission column for, in column order
 */
export function exportAssistantList(assistants: AssistantCoach[], components: CoachComponent[]) {
  if (assistants.length === 0) {
    return;
  }

  const exportData = assistants.map(assistant => ({
    ...assistantInfo(assistant),
    ...assistantPermissions(assistant, components),
  }));

  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename: `SocialCoachAssistants`,
    title: `SocialCoach Assistants`,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(exportData);
}

export function convertToAdminPlayerExport(player: PlayerSearchResponse): ExportAdminPlayerObject {
  return {
    email: player.email,
    phone: player.phone,
    firstName: player.firstName,
    lastName: player.lastName,
    verificationDate: player.verificationDate,
    coachEmail: player.coachProfile?.email,
    subscriptionStatus: player.subscriptionSummary?.status,
    tier: player.subscriptionSummary?.tier,
    type: player.subscriptionSummary?.type,
  };
}

export function exportCoachesList(coaches: CompleteAccount[]): ExportCoachObject[] {
  return coaches.map(coach => {
    const { profile, user } = coach;

    return {
      name: profile ? userFullName(profile!!) : "",
      email: user.email || "-",
      addedDate: user.createdDate,
      status: user.status,
    };
  });
}

export function exportComplianceEntries(
  entries: ComplianceExportEntry[],
  playerList: { [key: string]: SubscribedPlayerSearchResponse }
): ExportComplianceEntryObject[] {
  const exportList: ExportComplianceEntryObject[] = [];

  entries.map(entry => {
    const player = playerList[entry.playerId];
    const playerName = getFullName(player?.playerAccount?.firstName, player?.playerAccount?.lastName);
    const playerEmail = player?.playerAccount?.email;
    const playerInternalID = player?.playerAccount?.internalIdentifier;
    const playerBranch = player?.branchNames?.join(", ");
    const playerRegion = player?.regionNames?.join(", ");

    exportList.push({
      postDate: toDateTime(entry.postedTimestamp),
      socialNetwork: entry.socialNetworkId,
      playerName: playerName || "",
      playerEmail: playerEmail || "",
      internalIdentifier: playerInternalID || "",
      branch: playerBranch || "",
      region: playerRegion || "",
      sourceType: entry.sourceType || "",
      postLink: entry.externalPostUrl || "",
      postCaption: entry.caption || "",
      mediaType: entry.mediaCategory || "",
      mediaLink: entry.mediaUri || "",
      mediaTranscript: entry.mediaTranscript || "",
      keywords: entry.keyphrases || "",
      reviewStatus: entry.reviewStatus || "",
      reviewDate: toDateTime(entry.reviewedStatusDate),
    });
  });

  return exportList;
}

const toDateTime = (date?: string): string => {
  return date ? moment(date).tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format("MM/DD/YYYY h:mm a zz") : "";
};

export function exportPostsStatsToCsv(filename: string = "", stats: StatsResponse): void {
  // Only design breakdowns carry post ids. The CSV takes its columns from the first row, so the
  // column is decided per export, not per row.
  const withPostId = "designPackageId" in stats;
  const data = stats.breakdown.map(postStats => {
    return {
      // Rows of the same post share a postId, so the posts can be grouped back into uses.
      ...(withPostId ? { postId: postStats.postId ?? "" } : {}),
      playerEmail: postStats.playerEmail,
      playerName: postStats.playerName,
      ...(postStats.promptId
        ? {
            promptId: postStats.promptId,
            promptTitle: postStats.promptTitle,
          }
        : {}),
      socialNetworkId: postStats.socialNetworkId,
      status: postStats.status,
      postedTimestamp: toDateTime(postStats.postedTimestamp),
      externalPostUrl: postStats.externalPostUrl,

      // Blank means the network didn't report the metric; a reported zero stays 0.
      angerCount: postStats.latestStats?.angerCount ?? "",
      celebrateCount: postStats.latestStats?.celebrateCount ?? "",
      hahaCount: postStats.latestStats?.hahaCount ?? "",
      insightfulCount: postStats.latestStats?.insightfulCount ?? "",
      likeCount: postStats.latestStats?.likeCount ?? "",
      loveCount: postStats.latestStats?.loveCount ?? "",
      sorryCount: postStats.latestStats?.sorryCount ?? "",
      supportCount: postStats.latestStats?.supportCount ?? "",
      wowCount: postStats.latestStats?.wowCount ?? "",
      totalReactions: postStats.latestStats?.totalReactions ?? "",

      impressionCount: postStats.latestStats?.impressionCount ?? "",
      reachCount: postStats.latestStats?.reachCount ?? "",
      shareCount: postStats.latestStats?.shareCount ?? "",
      viewCount: postStats.latestStats?.viewCount ?? "",
      clickCount: postStats.latestStats?.clickCount ?? "",
      commentCount: postStats.latestStats?.commentCount ?? "",

      lastStatsSync: toDateTime(postStats.latestStats?.timestamp),
    };
  });

  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename: filename,
    title: filename,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(data);
}

const LEADERBOARD_HEADER_MAP: Record<string, string> = {
  // Base metrics
  impressionCount: "Impressions",
  reachCount: "Reach",
  viewCount: "Views",
  clickCount: "Clicks",
  likeCount: "Likes",
  commentCount: "Comments",
  shareCount: "Shares",
  totalReactions: "Total Reactions",
  loveCount: "Love Reactions",
  hahaCount: "Laugh Reactions",
  insightfulCount: "Insightful Reactions",
  supportCount: "Support Reactions",
  celebrateCount: "Celebrate Reactions",
  wowCount: "Wow Reactions",
  sorryCount: "Sorry Reactions",
  angerCount: "Anger Reactions",

  // Identity / Entity Info
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email Address",
  name: "Name",
  title: "Title",
  username: "Username",
  internalIdentifier: "Internal ID",
  displayName: "Display Name",

  // Post Info
  postedCaption: "Post Caption",
  postedTimestamp: "Date Posted",
  externalPostUrl: "External Post Link",
  socialNetworkId: "Social Network",

  // Custom Links (generated in your function)
  playerLink: "SocialCoach Player Link",
  promptLink: "SocialCoach Prompt Link",
  campaignLink: "SocialCoach Campaign Link",
};

const formatHeaderFallback = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1") // insert a space before all caps
    .replace(/^./, str => str.toUpperCase()) // uppercase the first character
    .trim();
};

export function exportLeaderboardToCsv(title: string = "", metric: LeaderboardMetric, data: LeaderboardResult[]): void {
  const modifiedData = data.map(item => {
    const id = item.id;

    // 1. Generate Links
    const link =
      item.type === "player"
        ? `${window.location.origin}${URLS.coach.editPlayer.replace(":playerId", id.toString())}`
        : item.type == "prompt"
        ? `${window.location.origin}${URLS.coach.prompt.replace(":promptId", id.toString())}`
        : item.type == "campaign"
        ? `${window.location.origin}${URLS.coach.editCampaigns.replace(":campaignId", id.toString())}`
        : item.type == "video_script"
        ? `${window.location.origin}${URLS.videoScript.replace(":id", id.toString())}`
        : undefined;

    // 2. Clone the item so we can manipulate it safely
    const rawData: any = { ...item };

    // Inject the score dynamically using the metric name as the key
    if (item.score !== undefined) {
      rawData[camelCase(metric)] = item.score;
    }

    if (link?.length) {
      const typeTitle = item.type
        .split("_")
        .map(t => capitalize(t))
        .join(" ");
      rawData[`${typeTitle}Link`] = link;
    }

    // Replace playerId with playerLink
    if ("playerId" in rawData) {
      const playerId = rawData["playerId"].toString();
      rawData["playerLink"] = `${window.location.origin}${URLS.coach.editPlayer.replace(":playerId", playerId)}`;
      delete rawData["playerId"];
    }

    // Cleanup internal IDs and redundant score fields
    delete rawData["id"];
    delete rawData["score"];
    delete rawData["type"]; // Usually redundant in a single-type export

    // 3. Rebuild the object with Human-Readable Keys
    const presentationData: Record<string, any> = {};

    for (const key in rawData) {
      if (Object.prototype.hasOwnProperty.call(rawData, key)) {
        let value = rawData[key];

        // Clean up nulls
        if (value === null) {
          value = "";
        }

        // Format Timestamps (Excel prefers standard locale strings over ISO formats)
        if (["postedTimestamp", "createdDate"].includes(key) && value) {
          value = new Date(value).toLocaleString();
        }

        // Map the internal key to the friendly header
        const friendlyHeader = LEADERBOARD_HEADER_MAP[key] || formatHeaderFallback(key);
        presentationData[friendlyHeader] = value;
      }
    }

    return presentationData;
  });

  const filename = title.replaceAll(" ", "").replaceAll(".", "");

  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename,
    title,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(modifiedData);
}

/**
 * Generates a csv file with Coaches info using the data sent by parameter
 * @param filename Name of the file that going to be generated
 * @param exportData List of elements in the correct format (ExportCoachObject) to export
 */
export function exportCoachesToCsv(filename: string = "", exportData: ExportCoachObject[]): void {
  if (exportData.length === 0) {
    return;
  }
  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename: `${filename}_coaches`,
    title: `${filename} coaches`,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);

  csvExporter.generateCsv(exportData);
}

export function exportDataToCsv(filename: string = "", exportData: {}[]): void {
  if (exportData.length === 0) {
    return;
  }
  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename: `${filename}`,
    title: `${filename}`,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(exportData);
}

export function exportComplianceEntriesToCsv(title: string, exportData: ExportComplianceEntryObject[]): void {
  if (exportData.length === 0) {
    return;
  }
  const options = {
    ...DEFAULT_CSV_OPTIONS,
    title,
    filename: title.replaceAll(" ", ""),
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(exportData);
}

export function exportVCUsageToCsv(filename: string = "", exportData: VideoProjectPlayerUsage[]): void {
  if (exportData.length === 0) {
    return;
  }
  const options = {
    ...DEFAULT_CSV_OPTIONS,
    filename: `vc_credit_usage-${filename}`,
    title: `${filename} VideoCatalyst Credit Usage`,
    useKeysAsHeaders: false,
    headers: [
      "Player Email",
      "Player Name",
      "VideoCatalyst Enabled",
      "Current Video Credit Limit",
      "Video Credits Used",
    ],
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(
    exportData.map(row => {
      return {
        "Player Email": row.playerEmail,
        "Player Name": row.playerName,
        "VideoCatalyst Enabled": row.currentlyEnabled ? "Yes" : "No",
        "Current Video Credit Limit": row.currentCreditLimit || "Unlimited",
        "Video Credits Used": row.creditsUsed,
      };
    })
  );
}
